import { useState, type ReactNode } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  pickLocalizedText,
  type Exercise,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { useBreakpoint } from "../useBreakpoint";
import { useCatalogItems } from "./useCatalogItems";
import { CatalogListView } from "./CatalogListView";
import { CatalogDetailView } from "./CatalogDetailView";

// Responsive Catalog composition for the web-rnw console. Mobile does NOT
// use this — it's always narrow and pushes a native CatalogDetail screen
// (behaviour-preserving). Here the SAME shared list + detail views compose
// by viewport:
//   wide   → master-detail (list pane left, detail pane right; tap fills
//            the right pane, no page change).
//   narrow → list → in-place detail with a back row (state push).
// Catalog data comes from the shared useCatalogItems (no offline cache on
// web → straight to network).

const LIST_PANE_WIDTH = 380;

export type CatalogBrowseProps = {
  theme: UiTheme;
  apiFetch: ProgramApiFetch;
  unit: "kg" | "lb";
  formatDate: (iso: string, locale: string) => string;
  /** Host-injected pain-risk dot per row (mobile: PainDot). Same optional
   *  slot CatalogListView already exposes — CatalogBrowse just forwards
   *  it, same as its sibling mounts do for the shared ExercisePicker. */
  renderPainDot?: (ex: Exercise) => ReactNode;
  /** What the dot SAYS — forwarded straight through, see CatalogListView's
   *  own doc on why this is a plain function, not a hook. */
  getPainRiskLabel?: (ex: Exercise) => string | null;
};

export function CatalogBrowse({
  theme,
  apiFetch,
  unit,
  formatDate,
  renderPainDot,
  getPainRiskLabel,
}: CatalogBrowseProps) {
  const { t, i18n } = useTranslation();
  const { wide } = useBreakpoint();
  const locale = coerceLocale(i18n.language);
  const styles = makeStyles(theme);

  const catalog = useCatalogItems({ apiFetch });
  const exercises: Exercise[] | null = catalog.raw ? [...catalog.raw] : null;
  const status = catalog.raw === null ? "loading" : "ready";

  const [selected, setSelected] = useState<string | null>(null);
  const selectedEx = catalog.raw?.find((e) => e.slug === selected) ?? null;
  const exName = (ex: Exercise) => pickLocalizedText(ex.name, locale);

  const list = (selectedSlug: string | null) => (
    <CatalogListView
      theme={theme}
      exercises={exercises}
      status={status}
      errorCode={null}
      selectedSlug={selectedSlug}
      onSelect={setSelected}
      compactFilters
      renderPainDot={renderPainDot}
      getPainRiskLabel={getPainRiskLabel}
    />
  );

  const detail = (ex: Exercise) => (
    <CatalogDetailView
      theme={theme}
      exercise={ex}
      unit={unit}
      apiFetch={apiFetch}
      formatDate={formatDate}
    />
  );

  if (wide) {
    return (
      <View style={styles.row}>
        <View style={styles.listPane}>{list(selected)}</View>
        <View style={styles.detailPane}>
          {selectedEx ? (
            <>
              <Text style={styles.paneHeading} numberOfLines={1}>
                {exName(selectedEx)}
              </Text>
              {detail(selectedEx)}
            </>
          ) : (
            <View style={styles.empty}>
              <Text style={styles.emptyGlyph}>‹</Text>
              <Text style={styles.emptyTitle}>
                {t("mobile.catalog.selectPrompt", {
                  defaultValue: "Select an exercise to see its details.",
                })}
              </Text>
            </View>
          )}
        </View>
      </View>
    );
  }

  // Narrow: list, or the selected exercise's detail with a back row.
  if (selectedEx) {
    return (
      <View style={styles.flex}>
        <View style={styles.backHeader}>
          <Pressable
            accessibilityRole="button"
            hitSlop={8}
            onPress={() => setSelected(null)}
          >
            <Text style={styles.back}>
              ‹ {t("mobile.catalog.back", { defaultValue: "Back" })}
            </Text>
          </Pressable>
          <Text style={styles.backTitle} numberOfLines={1}>
            {exName(selectedEx)}
          </Text>
          <View style={styles.backSpacer} />
        </View>
        {detail(selectedEx)}
      </View>
    );
  }
  return list(null);
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    flex: { flex: 1 },
    row: { flex: 1, flexDirection: "row" },
    listPane: {
      width: LIST_PANE_WIDTH,
      flexShrink: 0,
      borderRightWidth: 1,
      borderRightColor: p.border,
    },
    detailPane: { flex: 1, minWidth: 0 },
    paneHeading: {
      color: p.ink,
      fontSize: t.sizes.xl,
      fontWeight: "700",
      letterSpacing: -0.4,
      paddingHorizontal: s.s5,
      paddingTop: s.s4,
      paddingBottom: s.s3,
    },
    empty: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s5,
      gap: s.s3,
    },
    // A real placeholder, not a blank pane: a directional cue toward the
    // list + a readable instruction at body contrast (ink2, ≥4.5:1) — not
    // the faint ink3 line that read as empty.
    emptyGlyph: {
      color: p.ink3,
      fontSize: 44,
      lineHeight: 44,
      fontWeight: "300",
    },
    emptyTitle: {
      color: p.ink2,
      fontSize: t.sizes.lg,
      fontWeight: "600",
      textAlign: "center",
      maxWidth: 280,
    },
    backHeader: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s3,
      gap: s.s4,
    },
    back: { color: p.ink2, fontSize: t.sizes.base, minHeight: 44, lineHeight: 44 },
    backTitle: {
      flex: 1,
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
      textAlign: "center",
    },
    backSpacer: { width: 56 },
  });
}
